/**
 * ClientOnboardingReportingRequirements Component
 * Collects reporting requirements and data delivery preferences
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OnboardingProgressBar } from '../../components/ui';
import { useBeforeUnload } from '../../hooks';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboarding.module.css';

const ClientOnboardingReportingRequirements = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        regulatorySubmission: {
            required: false,
            method: {
                fax: false,
                email: false,
                writeOnForms: false
            }
        },
        specialInstructions: '',
        expediteCharges: false,
        electronicFormat: {
            needed: false,
            formats: {
                pdf: false,
                excelFile: false,
                csvFile: false,
                other: false,
                otherText: ''
            }
        },
        formattingRequirement: ''
    });

    const [errors, setErrors] = useState({});
    const [isSaved, setIsSaved] = useState(true);
    const [initialData, setInitialData] = useState(null);

    useEffect(() => {
        const loadSavedData = async () => {
            try {
                // Try to load from backend first
                const progress = await publicTokenService.getProgress(token);
                const actualProgress = progress.progress || progress;
                
                if (actualProgress && actualProgress.stepData && actualProgress.stepData.step5) {
                    setFormData(actualProgress.stepData.step5);
                    setInitialData(JSON.stringify(actualProgress.stepData.step5));
                    return;
                }
            } catch (error) {
                // Fallback to sessionStorage
            }
            
            // Fallback to sessionStorage
            const saved = sessionStorage.getItem(`onboarding_reporting_requirements_${token}`);
            if (saved) {
                const parsedData = JSON.parse(saved);
                setFormData(parsedData);
                setInitialData(saved);
            } else {
                setInitialData(JSON.stringify(formData));
            }
        };
        
        loadSavedData();
    }, [token]);

    // Track unsaved changes
    useEffect(() => {
        if (initialData) {
            const hasChanges = JSON.stringify(formData) !== initialData;
            setIsSaved(!hasChanges);
        }
    }, [formData, initialData]);

    // Warn before leaving if unsaved changes
    useBeforeUnload(!isSaved, 'You have unsaved changes. Please save your progress before leaving.');

    const handleCheckboxChange = (section, field, value) => {
        if (section) {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        
        // Clear errors
        if (errors[`${section}_${field}`] || errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`${section}_${field}`];
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleNestedCheckboxChange = (parent, section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [section]: {
                    ...prev[parent][section],
                    [field]: value
                }
            }
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // If regulatory submission is required, check method
        if (formData.regulatorySubmission.required) {
            const hasMethod = Object.values(formData.regulatorySubmission.method).some(v => v === true);
            if (!hasMethod) {
                newErrors.regulatoryMethod = 'Please select how to submit to regulatory agency';
            }
        }

        // If electronic format is needed, check at least one format
        if (formData.electronicFormat.needed) {
            const hasFormat = Object.entries(formData.electronicFormat.formats)
                .some(([key, value]) => key !== 'otherText' && value === true);
            if (!hasFormat) {
                newErrors.electronicFormat = 'Please select at least one electronic format';
            }
            
            if (formData.electronicFormat.formats.other && !formData.electronicFormat.formats.otherText.trim()) {
                newErrors.electronicFormatOther = 'Please specify the format';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveAndExit = async () => {
        try {
            // Save to backend (step 5: Reporting Requirements)
            await publicTokenService.saveProgress(token, {
                stepNumber: 5,
                stepData: formData,
                markStepComplete: false,
                moveToNextStep: false
            });
            
            // Save current data without validation
            sessionStorage.setItem(`onboarding_reporting_requirements_${token}`, JSON.stringify(formData));
            setIsSaved(true);
            setInitialData(JSON.stringify(formData));
            alert('Your progress has been saved. You can return using the same link to continue.');
        } catch (error) {
            console.error('Failed to save progress:', error);
            alert('Failed to save progress. Please try again.');
        }
    };

    const handleContinue = async () => {
        if (validateForm()) {
            try {
                // Save to backend and mark step complete (step 5: Reporting Requirements)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 5,
                    stepData: formData,
                    markStepComplete: true,
                    moveToNextStep: true
                });
                
                // Save to session storage
                sessionStorage.setItem(`onboarding_reporting_requirements_${token}`, JSON.stringify(formData));
                setIsSaved(true);
                
                // Navigate to QC/Disposal
                navigate(`/client-onboarding/${token}/qc-disposal`);
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save. Please try again.');
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleGoBack = () => {
        navigate(`/client-onboarding/${token}/billing-reporting`);
    };

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.logoHeader}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoHeaderImg}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.formContainer}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>Reporting Requirements</h1>
                            <p className={styles.subtitle}>
                                Please specify your data delivery and reporting needs
                            </p>
                        </div>

                        <form className={styles.form}>
                            {/* Results Delivery Information */}
                            <div className={styles.infoTile}>
                                <div className={styles.infoHeader}>
                                    <span className={styles.infoIcon}>ℹ️</span>
                                    <h3 className={styles.infoTitle}>How You'll Receive Your Results</h3>
                                </div>
                                <div className={styles.infoContent}>
                                    <p>WETLAB provides analytical results through two convenient methods:</p>
                                    <ul className={styles.infoList}>
                                        <li><strong>Email:</strong> Results will be sent to your designated reporting contact(s)</li>
                                        <li><strong>Client Portal:</strong> Access results anytime through our secure online portal. Once your account is created your contacts will receive an invitiation to create their portal accounts.</li>
                                    </ul>
                                </div>
                            </div>

                            {/* Regulatory Submission */}
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Regulatory Submission</h3>
                                <div className={styles.questionGroup}>
                                    <label className={styles.questionLabel}>Do results need to be submitted to a regulatory agency?</label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="regulatoryRequired"
                                                checked={formData.regulatorySubmission.required === true}
                                                onChange={() => handleCheckboxChange('regulatorySubmission', 'required', true)}
                                                className={styles.radio}
                                            />
                                            <span>Yes</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="regulatoryRequired"
                                                checked={formData.regulatorySubmission.required === false}
                                                onChange={() => handleCheckboxChange('regulatorySubmission', 'required', false)}
                                                className={styles.radio}
                                            />
                                            <span>No</span>
                                        </label>
                                    </div>
                                </div>
                                
                                {formData.regulatorySubmission.required === true && (
                                    <div className={styles.subSection}>
                                        <label className={styles.label}>If yes, please submit to:</label>
                                        <div className={styles.checkboxGrid}>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.regulatorySubmission.method.email}
                                                    onChange={(e) => handleNestedCheckboxChange('regulatorySubmission', 'method', 'email', e.target.checked)}
                                                    className={styles.checkbox}
                                                />
                                                <span>Email</span>
                                            </label>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.regulatorySubmission.method.writeOnForms}
                                                    onChange={(e) => handleNestedCheckboxChange('regulatorySubmission', 'method', 'writeOnForms', e.target.checked)}
                                                    className={styles.checkbox}
                                                />
                                                <span>CA Write on Forms</span>
                                            </label>
                                        </div>
                                        {errors.regulatoryMethod && (
                                            <span className={styles.errorText}>{errors.regulatoryMethod}</span>
                                        )}
                                    </div>
                                )}

                                {formData.regulatorySubmission.required === true && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Special Instructions:</label>
                                        <textarea
                                            value={formData.specialInstructions}
                                            onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                                            className={styles.textarea}
                                            placeholder="Any special instructions for regulatory submission..."
                                            rows="3"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Electronic Format */}
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Electronic Data Deliverables</h3>
                                <div className={styles.questionGroup}>
                                    <label className={styles.questionLabel}>Do you need a specific EDD (Electronic Data Deliverable) format?</label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="electronicFormatNeeded"
                                                checked={formData.electronicFormat.needed === true}
                                                onChange={() => handleCheckboxChange('electronicFormat', 'needed', true)}
                                                className={styles.radio}
                                            />
                                            <span>Yes</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="electronicFormatNeeded"
                                                checked={formData.electronicFormat.needed === false}
                                                onChange={() => handleCheckboxChange('electronicFormat', 'needed', false)}
                                                className={styles.radio}
                                            />
                                            <span>No</span>
                                        </label>
                                    </div>
                                </div>
                                
                                {formData.electronicFormat.needed && (
                                    <div className={styles.subSection}>
                                        <label className={styles.label}>If yes, what format?</label>
                                        <div className={styles.checkboxGrid}>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.electronicFormat.formats.excelFile}
                                                    onChange={(e) => handleNestedCheckboxChange('electronicFormat', 'formats', 'excelFile', e.target.checked)}
                                                    className={styles.checkbox}
                                                />
                                                <span>Excel File</span>
                                            </label>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.electronicFormat.formats.csvFile}
                                                    onChange={(e) => handleNestedCheckboxChange('electronicFormat', 'formats', 'csvFile', e.target.checked)}
                                                    className={styles.checkbox}
                                                />
                                                <span>CSV File</span>
                                            </label>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.electronicFormat.formats.other}
                                                    onChange={(e) => handleNestedCheckboxChange('electronicFormat', 'formats', 'other', e.target.checked)}
                                                    className={styles.checkbox}
                                                />
                                                <span>Other</span>
                                            </label>
                                        </div>
                                        {errors.electronicFormat && (
                                            <span className={styles.errorText}>{errors.electronicFormat}</span>
                                        )}
                                        
                                        {formData.electronicFormat.formats.other && (
                                            <input
                                                type="text"
                                                value={formData.electronicFormat.formats.otherText}
                                                onChange={(e) => handleNestedCheckboxChange('electronicFormat', 'formats', 'otherText', e.target.value)}
                                                className={`${styles.input} ${styles.otherInput} ${errors.electronicFormatOther ? styles.inputError : ''}`}
                                                placeholder="Please specify format"
                                            />
                                        )}
                                        {errors.electronicFormatOther && (
                                            <span className={styles.errorText}>{errors.electronicFormatOther}</span>
                                        )}
                                        
                                        <div className={styles.formGroup}>
                                            <label className={styles.label}>Formatting Requirement:</label>
                                            <textarea
                                                value={formData.formattingRequirement}
                                                onChange={(e) => handleInputChange('formattingRequirement', e.target.value)}
                                                className={styles.textarea}
                                                placeholder="Describe any specific formatting requirements..."
                                                rows="3"
                                            />
                                            <p className={styles.helpText}>
                                                <em>If you have a specific formatting requirement for use with a database, portal, or other data submission tool, you will need to provide an example of the required format or a data definition document.</em>
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    onClick={handleGoBack}
                                    className={styles.backButton}
                                >
                                    ← Go Back
                                </button>
                                <div className={styles.rightActions}>
                                    <button
                                        type="button"
                                        onClick={handleSaveAndExit}
                                        className={styles.saveButton}
                                    >
                                        💾 Save and Continue Later
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleContinue}
                                        className={styles.continueButton}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <p className={styles.footerText}>
                    Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                </p>
            </div>
        </React.Fragment>
    );
};

export default ClientOnboardingReportingRequirements;
